
import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import axios from '../services/AxiosInstance';
import { getApiUrl } from '../utils/ApiUtils';
import { useSettings } from './SettingsContext';

// Default accounting settings
const defaultAccountingSettings = {
    fiscalYear: {
        startMonth: 1,
        startDay: 1,
        endMonth: 12,
        endDay: 31
    },
    accountingMethod: 'accrual', // or 'cash'
    vatReportingPeriod: 'quarterly', // or 'monthly', 'annually'
    accountPlans: {
        selectedPlan: 'BAS2023',
        customizedAccounts: {}
    },
    journalEntrySettings: {
        requireDescription: true,
        requireDocuments: false,
        allowFutureDated: true,
        requireApproval: false
    }
};

const AccountingSettingsContext = createContext({
    accountingSettings: defaultAccountingSettings,
    isLoading: false,
    getAccountingMethodForDate: () => 'accrual'
});

/**
 * Normalize a date value (string, Date object, or ISO timestamp) to 'YYYY-MM-DD'.
 */
function toDateStr(val) {
    if (!val) return '';
    if (val instanceof Date) return val.toISOString().slice(0, 10);
    return String(val).slice(0, 10);
}

/**
 * Find the fiscal year that covers a given date string (YYYY-MM-DD).
 * Returns undefined if none matches.
 */
function findFiscalYearForDate(fiscalYears, dateStr) {
    if (!fiscalYears?.length || !dateStr) return undefined;
    const d = toDateStr(dateStr);
    return fiscalYears.find(fy => toDateStr(fy.start_date) <= d && toDateStr(fy.end_date) >= d);
}

/**
 * Get the most recent fiscal year by end_date (fallback when no FY covers today).
 */
function getMostRecentFiscalYear(fiscalYears) {
    if (!fiscalYears?.length) return undefined;
    return [...fiscalYears].sort((a, b) => toDateStr(b.end_date).localeCompare(toDateStr(a.end_date)))[0];
}

export const AccountingSettingsProvider = ({ children }) => {
    const { companyId, registerRefreshFunction } = useSettings();
    const [accountingSettings, setAccountingSettings] = useState(defaultAccountingSettings);
    const [fiscalYears, setFiscalYears] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const fetchAccountingSettings = async () => {
        if (!companyId) return;
        try {
            setIsLoading(true);
            const url = getApiUrl(`${companyId}/accounting/fiscalyears`);
            const { data } = await axios.get(url, { withCredentials: true });

            const years = data?.fiscalYears || data || [];
            setFiscalYears(years);

            // Determine current accounting method from fiscal years
            const today = new Date().toISOString().slice(0, 10);
            const currentFY = findFiscalYearForDate(years, today) || getMostRecentFiscalYear(years);
            const method = currentFY?.accounting_method || 'accrual';

            setAccountingSettings(prev => ({
                ...prev,
                accountingMethod: method
            }));
        } catch (error) {
            console.error('Error fetching fiscal years for accounting settings:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Fetch on mount / companyId change
    useEffect(() => {
        if (companyId) {
            fetchAccountingSettings();
        }
    }, [companyId]);

    // Register this context's refresh function with the parent
    useEffect(() => {
        const unregister = registerRefreshFunction('accountingSettings', fetchAccountingSettings);
        return unregister;
    }, [companyId]);

    /**
     * Look up the accounting method for a specific date.
     * Falls back to the current context method, then 'accrual'.
     */
    const getAccountingMethodForDate = useCallback((dateStr) => {
        const fy = findFiscalYearForDate(fiscalYears, dateStr);
        if (fy?.accounting_method) return fy.accounting_method;
        // Fallback to current context value
        return accountingSettings.accountingMethod;
    }, [fiscalYears, accountingSettings.accountingMethod]);

    return (
        <AccountingSettingsContext.Provider value={{
            accountingSettings,
            isLoading,
            getAccountingMethodForDate
        }}>
            {children}
        </AccountingSettingsContext.Provider>
    );
};
export const useAccountingSettings = () => useContext(AccountingSettingsContext);
export default AccountingSettingsContext;
